import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as R from 'ramda';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import CircularProgress from '@mui/material/CircularProgress';
import PropTypes from 'prop-types';
import { MDialog } from 'common';
import { format, isBefore, isValid, startOfToday } from 'date-fns';
import { AuthContext } from 'authentication/AuthContext';
import { useDispatch } from 'react-redux';
import { createSnack } from 'snackbar/snackbarSlice';
import { Add } from '@mui/icons-material';
import { Button, FormControlLabel, Stack, Switch } from '@mui/material';
import { CreateReconsignmentContext } from '../Create';
import SelectProductCard from './SelectProductCard';
import TitleBar from './TitleBar';
import ConfigurationStep from './ConfigurationStep';
import FromLocationsGrid from '../setup/fromLocations/FromLocationsGrid';
import ToLocationGrid from '../setup/toLocations/ToLocationsGrid';
import SummaryPage from '../summary/SummaryPage';
import { useCreateOnetoManyReconsignmentsMutation } from '../../reconsignmentsEndpoints';
import Confirmation from '../setup/confirmation/Confirmation';

const defaultState = {
  consignmentStatus: 'UNCONTROL',
  fromLocation: null,
  inventoryOwner: null,
  inventoryStatus: 'ALL',
  locationId: null,
  locationIdn: '',
  locationName: '',
  locationNumericId: null,
  origin: [],
  product: null,
  rvp: null,
  transferDate: null,
};

const CreateReconsignmentsWizard = ({ bulk, ...props }) => {
  const lgUp = useMediaQuery(theme => theme.breakpoints.up('lg'), { noSsr: true });
  const { affiliates } = useContext(AuthContext);
  const navigate = useNavigate();

  const [showOnlyLocationsWithVolume, setShowOnlyLocationsWithVolume] = useState(false);
  const { isDrawerOpen, setIsDrawerOpen } = useContext(CreateReconsignmentContext);
  const [initialState, setInitialstate] = useState(defaultState);
  const [reconsignmentState, setReconsignmentState] = useState(initialState);
  const [step, setStep] = useState(1);
  const [totalTransferVolume, setTotalTransferVolume] = useState(0);
  const [selectedOrigin, setSelectedOrigin] = useState([]);
  const [disableSave, setDisableSave] = useState(false);
  const [isVolumeExceeded, setIsVolumeExceeded] = useState(false);
  const [transferVolume] = useState(0);
  const [locationsInventoryVolumes, setLocationsInventoryVolumes] = useState([]);
  const [toLocations, setToLocations] = useState([]);
  const [toLocationGridKey, setToLocationGridKey] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createReconsignmentResults, setCreateReconsignmentResults] = useState([]);
  const [createReconsignmentInventoryDetails, setCreateReconsignmentInventoryDetails] = useState(
    []
  );
  const isDateInvalidOrPast = date => !isValid(date) || date < startOfToday();

  const [currentDate, setCurrentDate] = useState(startOfToday());
  if (isDateInvalidOrPast(currentDate)) {
    setCurrentDate(new Date());
    reconsignmentState.transferDate = new Date();
  } else if (isDateInvalidOrPast(reconsignmentState.transferDate)) {
    reconsignmentState.transferDate = currentDate;
  }

  const handleNext = () => {
    setStep(R.inc);
  };

  const handlePrev = () => {
    setStep(prev => prev - 1);
  };

  useEffect(() => {
    setCurrentDate(startOfToday());
  }, []);

  const handleLocationSelect = location => {
    setReconsignmentState(prev => ({
      ...prev,
      fromLocation: location || null,
      locationId: location ? location.locationIdn : null,
      locationName: location ? location.locationName : '',
      locationNumericId: location ? location.locationId : null,
    }));
    setToLocations([]);
    setLocationsInventoryVolumes([]);
  };

  useEffect(() => {
    handleLocationSelect(null);
  }, [reconsignmentState.inventoryOwner, reconsignmentState.product]);
  const [errors, setErrors] = useState({});

  // Determine if continue button should be enabled
  const canContinue = Boolean(
    reconsignmentState.inventoryOwner &&
      reconsignmentState.product &&
      reconsignmentState.locationId &&
      reconsignmentState.transferDate &&
      !errors.transferDate
  );

  const hasValidTransferVolume = R.and(R.gt(totalTransferVolume, 0), !isVolumeExceeded);

  const [confirmDialogOpened, setConfirmDialogOpened] = useState(false);

  useEffect(() => {
    if (!R.isEmpty(affiliates) && R.isNil(initialState.inventoryOwner)) {
      setInitialstate(R.assoc('inventoryOwner', affiliates[0]));
    }
  }, [affiliates, initialState.inventoryOwner, setInitialstate]);

  const validateField = (fieldName, value) => {
    const message = value ? '' : `${fieldName} is required`;
    setErrors(R.assoc(fieldName, message));
  };

  const validateTransferDate = date => {
    if (R.isNil(date)) {
      setErrors(R.assoc('transferDate', 'Date required'));
    } else if (date.toString() === 'Invalid Date') {
      setErrors(R.assoc('transferDate', 'Invalid Date'));
    } else if (isBefore(new Date(date), startOfToday())) {
      setErrors(
        R.assoc('transferDate', 'Transfer Date must be today or greater and in mm/dd/yyyy format')
      );
    } else {
      setErrors(R.assoc('transferDate', ''));
    }
  };

  const handleTransferDateChange = newValue => {
    validateTransferDate(newValue);
    if (R.isNil(newValue)) {
      setReconsignmentState(prev => ({ ...prev, transferDate: null }));
      setErrors(prev => ({ ...prev, transferDate: 'Transfer Date is required' }));
    } else if (newValue >= currentDate) {
      setReconsignmentState(prev => ({ ...prev, transferDate: newValue }));
    }
  };
  const handleInventoryStatusChange = (__, value) => {
    if (value === 'All') {
      setReconsignmentState({
        ...reconsignmentState,
        inventoryStatus: null,
      });
    } else {
      setReconsignmentState({
        ...reconsignmentState,
        inventoryStatus: value,
      });
    }
    handleLocationSelect(null);
    setToLocations([]);
  };

  const handleConsignmentStatusChange = (__, value) => {
    setReconsignmentState({
      ...reconsignmentState,
      consignmentStatus: value,
    });
    handleLocationSelect(null);
    setToLocations([]);
  };

  const closeDrawer = () => {
    setIsDrawerOpen({
      component: '',
      forceCloseSelector: () => false,
      holdOpenSelector: () => false,
      refreshNotifications: false,
      setCurrentDialogOpen: () => {},
      type: '',
    });
  };

  const hasChanges = R.not(R.equals(reconsignmentState, initialState));

  const onClose = event => {
    if (hasChanges) {
      event.preventDefault();
      setConfirmDialogOpened(true);
    } else {
      setConfirmDialogOpened(false);
      navigate('../../');
      closeDrawer();
    }
  };

  const onContinue = () => {
    setConfirmDialogOpened(false);
    navigate('../../');
    closeDrawer();
  };
  const handleNewReconsignment = () => {
    setReconsignmentState(defaultState);
    setStep(1);
    closeDrawer();
  };

  useEffect(() => {
    if (step === 1) {
      setDisableSave(!canContinue);
    } else if (step === 2) {
      setDisableSave(!hasValidTransferVolume);
    }
  }, [step, canContinue, hasValidTransferVolume]);

  const formatTransferDate = date => {
    return date ? format(date, 'MM/dd/yyyy') : 'No date is selected';
  };

  const [submitCreate] = useCreateOnetoManyReconsignmentsMutation();
  const dispatch = useDispatch();

  const handleSubmit = () => {
    setIsSubmitting(true); // Set loading state to true
    const rows = R.pipe(
      R.filter(
        R.pipe(R.prop('transferVolume'), R.both(R.complement(R.isNil), R.complement(R.isEmpty)))
      ),
      R.map(row => ({
        locationId: row.locationId,
        volume: row.transferVolume,
      }))
    )(toLocations);
    const data = {
      consignmentStatus:
        reconsignmentState.consignmentStatus === 'All'
          ? null
          : reconsignmentState.consignmentStatus === 'Control'
          ? 'CONTROL'
          : reconsignmentState.consignmentStatus === 'Uncontrol'
          ? 'UNCONTROL'
          : reconsignmentState.consignmentStatus,
      fromLocation: reconsignmentState.locationNumericId,
      inventoryOwner: {
        companyCustomerIdName: reconsignmentState.inventoryOwner.customerIdName,
        dataCompanyId: reconsignmentState.inventoryOwner.dataCompanyId,
      },
      inventoryStatus:
        reconsignmentState.inventoryStatus === 'All'
          ? null
          : reconsignmentState.inventoryStatus === 'Available'
          ? 'AVAILABLE'
          : reconsignmentState.inventoryStatus === 'In Transit'
          ? 'INTRANSIT'
          : reconsignmentState.inventoryStatus,
      originIds: selectedOrigin.map(R.prop('originId')),
      productId: reconsignmentState?.product?.productId,
      rvp: reconsignmentState?.rvp === 'Any' ? null : reconsignmentState?.rvp,
      toLocations: rows,
      transferDate: reconsignmentState.transferDate
        ? format(reconsignmentState.transferDate, "yyyy-MM-dd'T'00:00:00xxx")
        : null,
    };

    submitCreate({ body: data })
      .then(res => {
        if (res.error) {
          dispatch(
            createSnack({
              message: res.error.data.title,
              options: { variant: 'error' },
            })
          );
          setCreateReconsignmentInventoryDetails(res.data?.toLocationDetails || []);
        } else {
          setCreateReconsignmentResults(res.data?.createReconsignmentResults || []);
          setCreateReconsignmentInventoryDetails(res.data?.toLocationDetails || []);
          handleNext();
        }
      })
      .finally(() => {
        setIsSubmitting(false); // Set loading state to false
      });
  };

  const handleDirectClose = () => {
    setReconsignmentState(defaultState);
    setConfirmDialogOpened(false);
    closeDrawer();
    navigate('../../');
  };

  const formattedTransferDate = formatTransferDate(reconsignmentState?.transferDate);

  return (
    <>
      <TitleBar
        onClose={step === 4 ? handleDirectClose : onClose}
        title="Create New Reconsignment"
      />
      {step === 1 && (
        <>
          <ConfigurationStep
            errors={errors}
            handleConsignmentStatusChange={handleConsignmentStatusChange}
            handleDrawerClose={onClose}
            handleInventoryStatusChange={handleInventoryStatusChange}
            handleLocationSelect={handleLocationSelect}
            handleTransferDateChange={handleTransferDateChange}
            isDrawerOpen={isDrawerOpen}
            onClose={onClose}
            reconsignmentState={reconsignmentState}
            setErrors={setErrors}
            setIsDrawerOpen={setIsDrawerOpen}
            setReconsignmentState={setReconsignmentState}
            validateField={validateField}
            validateTransferDate={validateTransferDate}
            {...props}
            setSelectedOrigin={setSelectedOrigin}
          />
          <Box
            sx={{
              marginLeft: lgUp ? '395px' : 0,
              marginTop: 12,
              overflow: bulk ? 'auto' : 'visible',
              overflowX: bulk ? 'auto' : 'hidden',
              pb: 10,
              textAlign: 'center',
              width: bulk ? 'calc(100% - 395px)' : 'auto',
              zIndex: bulk ? 2 : 1000,
            }}
          >
            {reconsignmentState.product ? (
              <FromLocationsGrid
                onLocationSelect={handleLocationSelect}
                reconsignmentState={reconsignmentState}
                setTotalTransferVolume={setTotalTransferVolume}
                totalTransferVolume={totalTransferVolume}
              />
            ) : (
              <SelectProductCard
                subtitle="Start by selecting the required product information on the left."
                title="Select product information to populate available locations"
              />
            )}
          </Box>
        </>
      )}
      {step === 2 && (
        <ToLocationGrid
          formattedTransferDate={formattedTransferDate}
          gridKey={toLocationGridKey}
          isVolumeExceeded={isVolumeExceeded}
          reconsignmentState={reconsignmentState}
          selectedOrigin={selectedOrigin}
          setGridKey={setToLocationGridKey}
          setIsVolumeExceeded={setIsVolumeExceeded}
          setLocationsInventoryVolumes={setLocationsInventoryVolumes}
          setReconsignmentState={setReconsignmentState}
          setSelectedOrigin={setSelectedOrigin}
          setToLocations={setToLocations}
          setTotalTransferVolume={setTotalTransferVolume}
          setTransferVolume={setTotalTransferVolume}
          showOnlyLocationsWithVolume={showOnlyLocationsWithVolume}
          toLocations={toLocations}
          totalTransferVolume={totalTransferVolume}
        />
      )}

      {R.or(lgUp, R.not(isDrawerOpen)) && (
        <Stack
          alignItems="center"
          direction="row"
          spacing={2}
          sx={{
            backgroundColor: 'surface.white',
            bottom: 0,
            boxShadow: 0,
            height: 80,
            // eslint-disable-next-line sort-keys-fix/sort-keys-fix
            justifyContent: { xs: 'flex-end', lg: 'flex-end' },
            position: 'fixed',
            px: 3,
            top: 'auto',
            width: 1,
            zIndex: 1200,
          }}
        >
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <Box>
              {step === 2 && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={showOnlyLocationsWithVolume}
                      onChange={e => setShowOnlyLocationsWithVolume(e.target.checked)}
                    />
                  }
                  // unique id for WalkMe to target this control
                  id="show-zero-volume-locations-toggle"
                  label="Show locations with 0 volume"
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      mt: 0.25,
                    },
                  }}
                />
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                onClick={
                  step === 4
                    ? handleNewReconsignment
                    : step === 2 || step === 3
                    ? handlePrev
                    : onClose
                }
                startIcon={
                  step === 4 ? <Add sx={{ mb: '0.15rem', ml: 0.5, mr: 0.5 }} /> : undefined
                }
                variant="outlined"
              >
                {step === 4 ? 'New Reconsignment' : 'Back'}
              </Button>
              <Button
                disabled={disableSave || isSubmitting}
                endIcon={
                  isSubmitting && step === 3 ? (
                    <CircularProgress
                      size={20}
                      sx={{
                        marginLeft: 1,
                      }}
                    />
                  ) : undefined
                }
                onClick={
                  step < 3
                    ? handleNext
                    : step === 4
                    ? e => {
                        e?.preventDefault?.();
                        handleDirectClose();
                      }
                    : handleSubmit
                }
              >
                {step === 2
                  ? 'Review'
                  : step === 3
                  ? isSubmitting
                    ? 'Submitting'
                    : 'Submit'
                  : step === 4
                  ? 'Close'
                  : 'Continue'}
              </Button>
            </Box>
          </Box>
        </Stack>
      )}
      {step === 3 && (
        <SummaryPage
          formattedTransferDate={formattedTransferDate}
          locationsInventoryVolumes={locationsInventoryVolumes}
          onBack={handlePrev}
          onSubmit={onClose}
          reconsignmentState={reconsignmentState}
          selectedOrigin={selectedOrigin}
          toLocations={toLocations}
          totalTransferVolume={totalTransferVolume}
        />
      )}
      {step === 4 && (
        <Confirmation
          createReconsignmentInventoryDetails={createReconsignmentInventoryDetails}
          createReconsignmentResults={createReconsignmentResults}
          locationsInventoryVolumes={locationsInventoryVolumes}
          onBack={handlePrev}
          onSubmit={e => {
            e?.preventDefault?.();
            handleDirectClose();
          }}
          reconsignmentState={reconsignmentState}
          selectedOrigin={selectedOrigin}
          toLocations={toLocations}
          totalTransferVolume={transferVolume}
        />
      )}
      <MDialog
        header="Unsaved Changes"
        leftButtonAction={onContinue}
        leftButtonText="Continue"
        open={confirmDialogOpened}
        rightButtonAction={() => setConfirmDialogOpened(false)}
        rightButtonText="Keep Editing"
      >
        <Typography color="textPrimary" component="span" variant="subtitle1">
          Are you sure you want to leave this page? You have unsaved reconsignment changes.
        </Typography>
      </MDialog>
    </>
  );
};

CreateReconsignmentsWizard.propTypes = {
  bulk: PropTypes.bool,
};

CreateReconsignmentsWizard.defaultProps = {
  bulk: false,
};

export default CreateReconsignmentsWizard;
